import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { FactMetadataOutDTO, FactQuantifierEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { ComparisonNode, OperatorKey, RuleValue, valueSurvivesOperatorChange } from '../../ruleAst';
import { useRuleMetadata } from '../../metadata/RuleMetadataProvider';
import { argLabel, argValue, argValueLabel, factParametersOf, withArg, withFact } from '../../factArguments';
import FactPicker from './FactPicker';
import { listConditionHint, textConditionHint } from './valueDescriptor';
import ValueInput from './ValueInput';

interface ComponentProps {
  node: ComparisonNode;
  facts: FactMetadataOutDTO[];
  onChange: (node: ComparisonNode) => void;
  onRemove: () => void;
  onWrap: (op: 'AND' | 'OR') => void;
  onToggleNot: () => void;
  negated?: boolean;
  companyId?: string;
  readOnly?: boolean;
}

/** One test: fact -> operator -> value. */
const ConditionLeaf: React.FC<ComponentProps> = ({ node, facts, onChange, onRemove, onWrap, onToggleNot, negated, companyId, readOnly }) => {
  const { tr } = useTranslationLgs();
  const { factByKey, operatorByKey, operatorsForFact, operatorTakesValue } = useRuleMetadata();

  const fact = factByKey(node.fact);
  const operators = operatorsForFact(node.fact);
  const parameters = factParametersOf(fact);
  const listHint = listConditionHint(fact ? String(fact.factType) : undefined, node.operator);
  const textHint = textConditionHint(fact, node.operator);

  const changeFact = (factKey: string) => {
    // The old operator may not be legal for the new fact, and the old value and arguments almost
    // certainly do not fit it, so they are reset; a fact with arguments starts at their defaults.
    const legal = operatorsForFact(factKey);
    onChange(withFact(node, factKey, (legal[0]?.key as OperatorKey) ?? 'EQ', factParametersOf(factByKey(factKey))));
  };

  const changeOperator = (operator: OperatorKey) => {
    // Keep the value only when the new operator wants the same kind of one.
    const keep = valueSurvivesOperatorChange(
      { key: node.operator, valueList: operatorByKey(node.operator)?.valueEntityRefType },
      { key: operator, valueList: operatorByKey(operator)?.valueEntityRefType },
    );
    const value = keep ? node.value : undefined;
    onChange({ ...node, operator, value });
  };

  const changeValue = (value: RuleValue) => onChange({ ...node, value });

  return (
    <div className="flex flex-wrap items-start gap-2 rounded-md border border-solid border-base-tertiary bg-white p-2">
      {negated && <span className="mt-2 rounded bg-base-tertiary px-2 py-0.5 text-xs font-medium uppercase text-content-secondary">{tr('AiDispatcher.not', 'not')}</span>}

      <div className="min-w-[16rem] flex-1">
        <FactPicker facts={facts} value={node.fact} onChange={changeFact} disabled={readOnly} />
        {/* A fact with one value per location: ALL must hold at every one, ANY at one or more.
            Easy to misread under a negation — `allLocations … not in group EU` means no location
            is in the EU, `anyLocation … not in [A]` only that some stop is not A. */}
        {fact?.factQuantifier === FactQuantifierEnum.ALL && (
          <div className="mt-1 text-xs text-content-tertiary">{tr('AiDispatcher.factQuantifierAll', 'Must hold at every location')}</div>
        )}
        {fact?.factQuantifier === FactQuantifierEnum.ANY && (
          <div className="mt-1 text-xs text-content-tertiary">{tr('AiDispatcher.factQuantifierAny', 'Holds if at least one location matches')}</div>
        )}
        {/* A hand-typed or address-book stop has no company location: it is unknown, not
            "somewhere else", so it never matches — not even under `not`. */}
        {listHint && <div className="mt-1 text-xs text-content-tertiary">{tr(listHint.key, listHint.text)}</div>}
        {textHint && <div className="mt-1 text-xs text-content-tertiary">{tr(textHint.key, textHint.text)}</div>}
        {fact?.entityRefType === 'COMPANY_LOCATION' && (
          <div className="mt-1 text-xs text-content-tertiary">
            {tr('AiDispatcher.companyLocationHint', 'Stops not picked from the location list never match, not even with "not".')}
          </div>
        )}
      </div>

      {/* One dropdown per argument of a parameterised fact — which end of each stop to measure from. */}
      {parameters.map(parameter => (
        <div key={parameter.name} className="w-56">
          <Dropdown
            className="w-full"
            value={argValue(node, parameter)}
            options={parameter.parameterValues.map(value => ({ label: argValueLabel(tr, parameter, value), value }))}
            valueTemplate={option => (option ? `${argLabel(tr, parameter)}: ${option.label}` : argLabel(tr, parameter))}
            onChange={event => onChange(withArg(node, parameters, parameter.name, event.value))}
            disabled={readOnly}
          />
        </div>
      ))}

      <div className="w-40">
        <Dropdown
          className="w-full"
          value={node.operator}
          options={operators.map(operator => ({ label: tr(operator.i18nKey, operator.symbol), value: operator.key }))}
          onChange={event => changeOperator(event.value)}
          disabled={readOnly || !fact}
          placeholder={tr('AiDispatcher.selectOperator', 'Operator')}
        />
      </div>

      {/* IS_NULL / IS_NOT_NULL take no value at all, so no input is rendered for them. */}
      {fact && operatorTakesValue(node.operator) && (
        // No explicit min-width: the column may not shrink below its content, so a wide value (a
        // relative time, a date range) wraps the row's buttons to the next line instead of
        // running underneath them.
        <div className="max-w-full shrink grow basis-56">
          <ValueInput fact={fact} operator={node.operator} value={node.value ?? null} onChange={changeValue} companyId={companyId} disabled={readOnly} />
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center gap-1">
          <RingilButton
            mode="text-only"
            label={tr('AiDispatcher.wrapAnd', '+ and')}
            tooltip={tr('AiDispatcher.wrapAndTooltip', 'Group this condition with another using AND')}
            onClick={() => onWrap('AND')}
          />
          <RingilButton
            mode="text-only"
            label={tr('AiDispatcher.wrapOr', '+ or')}
            tooltip={tr('AiDispatcher.wrapOrTooltip', 'Group this condition with another using OR')}
            onClick={() => onWrap('OR')}
          />
          <RingilButton
            mode="text-only"
            label={tr('AiDispatcher.negate', 'not')}
            tooltip={tr('AiDispatcher.negateTooltip', 'Negate this condition')}
            onClick={onToggleNot}
          />
          <RingilButton mode="text-only" label={tr('AiDispatcher.remove', 'Remove')} onClick={onRemove} />
        </div>
      )}
    </div>
  );
};

export default ConditionLeaf;
